import { Box, Group, Skeleton } from "@mantine/core";
import { useNavigate, useParams } from "react-router-dom";

import FormCard from "src/components/FormCard";
import DiscountFormFields, {
  discountFormSchema,
} from "../components/FormFields";
import { useForm, yupResolver } from "@mantine/form";
import { ExpirationFields } from "../components/ExpirationField";
import {
  UpdateDiscountCodeInput,
  useDiscountCodeOneQuery,
  useGetLocationsQuery,
  useUpdateDiscountCodeMutation,
  DiscountCodeOneQuery,
  DiscountCodeType,
  useDeleteDiscountCodeMutation,
  useDiscountCodeAgreementsLazyQuery,
  useDiscountCodeProductsLazyQuery,
  DiscountCodeAgreementsQuery,
  DiscountCodeProductsQuery,
} from "src/generated/graphql";
import Switch from "src/components/Switch/Switch";
import { DiscountTypeList } from "../components/TypeList";
import { IIDAndName } from "src/utils/helperTypes";
import { useDisclosure, useSetState } from "@mantine/hooks";
import { EMPLOYEE_ROLE_OBJ, MESSAGES, ROUTES } from "src/utils/constants";
import useWritePermission from "src/hooks/usePermission";

export interface IDiscountForm
  extends Omit<
    UpdateDiscountCodeInput,
    "locations" | "agreements" | "products"
  > {
  locations: string[] | undefined;
  agreements: string[] | undefined;
  products: string[] | undefined;
}

export type TypeList = {
  [key in DiscountCodeType]: IIDAndName[];
};

const DiscountsDetails = () => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgDiscounts);
  const { discId = "" } = useParams<{ discId: string }>();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const [typeList, setTypeList] = useSetState<TypeList>({
    Agreement: [],
    Product: [],
  });
  const navigate = useNavigate();
  const form = useForm<IDiscountForm>({
    validate: yupResolver(discountFormSchema),
  });
  const { data: locationsArray, loading: locationLoading } =
    useGetLocationsQuery();

  const [updateDiscountCode, { loading: updateLoading }] =
    useUpdateDiscountCodeMutation();

  const [getDiscountAgreements, { loading: agreementsLoading }] =
    useDiscountCodeAgreementsLazyQuery();

  const [getDiscountProducts, { loading: productsLoading }] =
    useDiscountCodeProductsLazyQuery();

  const {
    loading,
    data: detailObj,
    refetch,
  } = useDiscountCodeOneQuery({
    variables: {
      discountCodeOneId: discId,
    },
    onCompleted: (data) => {
      const isAgreement =
        data.discountCodeOne.discountCodeType === DiscountCodeType?.Agreement
          ? true
          : false;

      let list;
      if (isAgreement) {
        getDiscountAgreements({
          variables: { discountCodeOneId: discId, isAgreement },
          fetchPolicy: "network-only",
          onCompleted: (agree) => {
            list = agree.discountCodeAgreementsOrProducts.agreements;
            handleFetchComplete(data.discountCodeOne, list, []);
          },
        });
      } else {
        getDiscountProducts({
          variables: { discountCodeOneId: discId, isAgreement },
          fetchPolicy: "network-only",
          onCompleted: (prod) => {
            list = prod.discountCodeAgreementsOrProducts.products;
            handleFetchComplete(data.discountCodeOne, [], list);
          },
        });
      }
    },
  });

  const [deleteDiscountCode, { loading: deleteLoading }] =
    useDeleteDiscountCodeMutation({
      onCompleted: () => {
        navigate(`${ROUTES.DISCOUNTS}`);
      },
    });

  const discountLoading =
    (loading && locationLoading && (agreementsLoading || productsLoading)) ||
    updateLoading ||
    deleteLoading;
  let isFormUpdated = !isFormActive && form.isDirty() && form.isTouched();
  const { value: redeemableValue, ...redeemableProps } =
    form.getInputProps("isStaffOnly");

  const handleFetchComplete = (
    detail: DiscountCodeOneQuery["discountCodeOne"],
    agreements: DiscountCodeAgreementsQuery["discountCodeAgreementsOrProducts"]["agreements"] = [],
    products: DiscountCodeProductsQuery["discountCodeAgreementsOrProducts"]["products"] = []
  ) => {
    const {
      __typename,
      // agreements,
      // products,
      locations,
      createdDate,
      updatedDate,
      startAt,
      deletedDate,
      id,
      expiration: {
        expiresAt,
        unit: expirationUnit,
        value: expirationValue,
        expirationMode,
      },
      description,
      ...restValues
    } = detail;
    setTypeList({
      Agreement: agreements,
      Product: products,
    });
    const fieldData = {
      locations: locations.map((location) => location.id),
      agreements: agreements.map((agreement) => agreement.id),
      products: products.map((product) => product.id),
      startAt: new Date(startAt),
      expiresAt: new Date(expiresAt),
      expirationUnit,
      expirationValue,
      mode: expirationMode,
      description: description || undefined,
      ...restValues,
    };
    form.setValues(fieldData);
    form.resetDirty(fieldData);
    form.resetTouched();
  };

  const handleDelete = () => {
    deleteDiscountCode({
      variables: {
        deleteDiscountCodeId: discId,
      },
    });
  };

  const handleSubmit = (values: Required<IDiscountForm>) => {
    const { locations, agreements, products, ...restValues } = values;
    updateDiscountCode({
      variables: {
        data: {
          ...restValues,
          locations: locations?.map((id) => ({ id })),
          agreements: agreements?.map((id) => ({ id })),
          products: products?.map((id) => ({ id })),
        },
        updateDiscountCodeId: discId,
      },
    }).then((resp) => {
      if (resp.data?.updateDiscountCode) {
        refetch();
        toggleFormActive();
      }
    });
  };

  const handleReset = async () => {
    handleFetchComplete(detailObj?.discountCodeOne!);
    toggleFormActive();
  };

  return (
    <FormCard
      title="Basic Info"
      loading={discountLoading}
      isFormActive={isFormActive}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleReset}
      onEdit={isAllowWrite ? toggleFormActive : undefined}
      onDelete={isAllowWrite ? handleDelete : undefined}
      onSave={form.onSubmit((values) => {
        handleSubmit(values as Required<IDiscountForm>);
      })}
    >
      {discountLoading ? (
        <Group>
          <Box w="48%">
            <Skeleton height={36} radius="xl" />
            <Skeleton height={36} mt={15} width="70%" radius="xl" />
            <Skeleton height={36} mt={15} width="20%" radius="xl" />
            <Skeleton height={36} mt={15} radius="xl" />
            <Skeleton height={36} mt={15} width="70%" radius="xl" />
            <Skeleton height={36} mt={15} width="20%" radius="xl" />
            <Skeleton height={36} mt={15} radius="xl" />
            <Skeleton height={36} mt={15} width="70%" radius="xl" />
            <Skeleton height={36} mt={15} width="20%" radius="xl" />
          </Box>
          <Box w="48%">
            <Skeleton height={36} radius="xl" />
            <Skeleton height={36} mt={15} width="70%" radius="xl" />
            <Skeleton height={36} mt={15} width="20%" radius="xl" />
            <Skeleton height={36} mt={15} radius="xl" />
            <Skeleton height={36} mt={15} width="70%" radius="xl" />
            <Skeleton height={36} mt={15} width="20%" radius="xl" />
            <Skeleton height={36} mt={15} radius="xl" />
            <Skeleton height={36} mt={15} width="70%" radius="xl" />
            <Skeleton height={36} mt={15} width="20%" radius="xl" />
          </Box>
        </Group>
      ) : (
        <Group position="apart" align="flex-start" grow>
          <Box>
            <DiscountFormFields
              locations={locationsArray?.locationMany?.map((val) => val)}
              form={form}
              hideType
              isActive={isFormActive}
            >
              <Switch
                labelPosition="left"
                disabled={isFormActive}
                label="Redeemable by staff only"
                checked={redeemableValue}
                {...redeemableProps}
              />
            </DiscountFormFields>
          </Box>
          <Box>
            <DiscountTypeList
              list={
                typeList![
                  form.getInputProps("discountCodeType")
                    .value as DiscountCodeType
                ]
              }
              isActive={isFormActive}
              onListUpdate={setTypeList}
              form={form}
            />
            <ExpirationFields<IDiscountForm>
              form={form}
              isActive={isFormActive}
            />
          </Box>
        </Group>
      )}
    </FormCard>
  );
};

export default DiscountsDetails;
